// Settings → Proposal Branding.
// Controls how proposals look and behave for this workspace.

import { useEffect, useState } from "react";
import { FileText, Save, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import PragmaSelect from "../components/PragmaSelect";
import ProposalImageUpload from "../components/ProposalImageUpload";

const CURRENCIES = [
  "USD", "EUR", "GBP", "AUD", "CAD", "NZD", "JPY", "INR", "SGD", "AED",
].map((c) => ({ value: c, label: c }));

export default function ProposalBrandingTab() {
  const [state, setState] = useState({ loading: true, saving: false, data: null });

  const load = async () => {
    try {
      const { data } = await api.get("/proposal-settings");
      setState({ loading: false, saving: false, data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load settings");
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => { load(); }, []);

  const patch = (updates) => setState((s) => ({ ...s, data: { ...s.data, ...updates } }));

  const save = async () => {
    setState((s) => ({ ...s, saving: true }));
    try {
      await api.put("/proposal-settings", {
        company_name: state.data.company_name,
        logo_url: state.data.logo_url,
        address: state.data.address,
        vat_id: state.data.vat_id,
        vat_percent: Number(state.data.vat_percent) || 0,
        default_currency: state.data.default_currency,
        auto_accept_workflow: !!state.data.auto_accept_workflow,
      });
      toast.success("Proposal branding saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setState((s) => ({ ...s, saving: false }));
    }
  };

  if (state.loading || !state.data) {
    return <div className="p-6 text-sm" style={{ color: "var(--pg-text-mute)" }}>Loading…</div>;
  }
  const d = state.data;

  return (
    <div className="max-w-3xl" data-testid="proposal-branding-tab">
      <h3 className="mb-1" style={{ fontSize: 18 }}>Proposal branding</h3>
      <p className="text-sm mb-6" style={{ color: "var(--pg-text-mute)" }}>
        These settings control how every proposal, its PDF export, and its client-facing web link appear.
      </p>

      {/* Header block */}
      <div className="pg-card p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={14} style={{ color: "var(--pg-accent)" }} />
          <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Cover</h4>
        </div>
        <Field label="Company name">
          <input
            className="pg-input w-full"
            value={d.company_name || ""}
            onChange={(e) => patch({ company_name: e.target.value })}
            data-testid="branding-company-name"
            placeholder="What clients see at the top of every proposal"
          />
        </Field>
        <div className="mt-4">
          <label className="block mb-1 text-xs" style={{ color: "var(--pg-text-mute)" }}>Logo</label>
          <ProposalImageUpload
            value={{ url: d.logo_url || "" }}
            onChange={({ url }) => patch({ logo_url: url })}
            kind="image"
            testId="branding-logo-upload"
            height={140}
          />
        </div>
        <div className="mt-4">
          <Field label="Business address">
            <textarea
              className="pg-input w-full"
              rows={2}
              value={d.address || ""}
              onChange={(e) => patch({ address: e.target.value })}
              data-testid="branding-address"
              placeholder="Street, city, country — appears under your company name"
            />
          </Field>
        </div>
      </div>

      {/* Money block */}
      <div className="pg-card p-6 mb-4">
        <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0, marginBottom: 12 }}>Numbers</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Default currency">
            <PragmaSelect
              value={d.default_currency}
              onChange={(v) => patch({ default_currency: v })}
              options={CURRENCIES}
              testId="branding-currency"
              size="sm"
            />
          </Field>
          <Field label="Tax rate (%)">
            <input
              type="number" min="0" step="0.01"
              className="pg-input w-full"
              value={d.vat_percent ?? 0}
              onChange={(e) => patch({ vat_percent: e.target.value })}
              data-testid="branding-vat-percent"
            />
          </Field>
          <Field label="Tax / VAT / GST ID">
            <input
              className="pg-input w-full"
              value={d.vat_id || ""}
              onChange={(e) => patch({ vat_id: e.target.value })}
              data-testid="branding-vat-id"
              placeholder="Optional"
            />
          </Field>
        </div>
      </div>

      {/* Workflow block */}
      <div className="pg-card p-6 mb-4">
        <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0, marginBottom: 6 }}>Acceptance workflow</h4>
        <p className="text-xs mb-4" style={{ color: "var(--pg-text-mute)" }}>
          What should happen the moment a client clicks Accept on a proposal.
        </p>
        <Toggle
          label="Automatically mark Deal as Won"
          hint="Also creates the client record and pushes proposal deliverables into the client's deliverables tab. Turn off if you'd rather run this manually with a 'Convert proposal' click."
          value={!!d.auto_accept_workflow}
          onChange={(v) => patch({ auto_accept_workflow: v })}
          testid="branding-auto-accept"
        />
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={state.saving}
          className="pg-btn pg-btn-primary"
          data-testid="branding-save-btn"
        >
          {state.saving ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <><Save size={13} /> Save</>}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block mb-1 text-xs" style={{ color: "var(--pg-text-mute)" }}>{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, hint, value, onChange, testid }) {
  return (
    <label className="flex items-start justify-between gap-4 py-2" style={{ cursor: "pointer" }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
        {hint && <div className="text-xs mt-0.5" style={{ color: "var(--pg-text-mute)" }}>{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testid}
        style={{
          width: 40, height: 22, borderRadius: 100,
          background: value ? "var(--pg-accent)" : "var(--pg-border)",
          border: "none", cursor: "pointer", position: "relative",
          transition: "background 140ms",
          flexShrink: 0, marginTop: 2,
        }}
      >
        <span style={{
          position: "absolute", top: 3, left: value ? 21 : 3,
          width: 16, height: 16, borderRadius: "50%",
          background: "#fff", transition: "left 140ms",
        }} />
      </button>
    </label>
  );
}
